import DropdownSheetHeader from 'LuxKit/Dropdown/Sheet/DropdownSheetHeader'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import type { ComponentProps, ReactNode } from 'react'
import { useContext, useMemo } from 'react'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import { getRecommendationsTitle } from './Recommendations/utils/recommendationsTitle'
import { useCalendarRecommendationsView } from './CalendarRecommendationsViewContext'
import Group from 'components/utils/Group'
import type { OfferPageFilterType } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarCapacitySelect from './CalendarCapacity/OfferCalendarCapacitySelect'
import { EmptyArray, nonNullable } from 'lib/array/arrayUtils'
import OfferCalendarRoomSelect from './CalendarRooms/OfferCalendarRoomSelect'
import { isVillaOffer } from 'lib/offer/offerTypes'
import OfferCalendarDurationSelect from './CalendarDuration/OfferCalendarDurationSelect'
import GeoContext from 'contexts/geoContext'
import useIsFlightsDisabledForGeoState from 'hooks/Flights/useIsFlightsDisabledForGeoState'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import OfferCalendarFlightsToggle from './OfferCalendarFlightsToggle'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import OfferCalendarLuxPlusToggle from 'luxPlus/components/OfferCalendarLuxPlusToggle'
import { isMultipleRoomBookingActive } from 'selectors/offerPage/offerPageSharedSelectors'
import { preventRenderWithClosedFilter } from 'lib/offer/offerPageUtil'
import { useIntl } from 'react-intl'
import OfferCalendarPointsToggle from './OfferCalendarPointsToggle'
import OfferCalendarDatePickerFlightDealBanner from './OfferCalendarDatePickerFlightDealBanner'
import OfferCalendarHeaderExtensionContainer from './OfferCalendarHeaderExtensionContainer'

interface Props extends Pick<ComponentProps<typeof DropdownSheetHeader>, 'onBackClick' | 'onDismissClick'> {
  subFilters?: Array<OfferPageFilterType>
  offer: App.Offer
  duration?: number
  rooms?: Array<App.Occupants>
  monthIndex?: string
  isMultipleRoomBookingActive: boolean
  isLuxPlusEnabled: boolean
  airport?: App.Airport
  isAnyFlightsActive: boolean
  isCalendarBurnToggleVisible: boolean
}

function OfferCalendarDatePickerDropdownHeader(props: Props) {
  const {
    onBackClick,
    onDismissClick,
    offer,
    subFilters = EmptyArray,
    duration,
    rooms = EmptyArray,
    monthIndex,
    isMultipleRoomBookingActive,
    isLuxPlusEnabled,
    airport,
    isAnyFlightsActive,
    isCalendarBurnToggleVisible,
  } = props

  const intl = useIntl()
  const { currentRegionHasFlights } = useContext(GeoContext)
  const isFlightsDisabledForGeoState = useIsFlightsDisabledForGeoState()

  const hasSelectedMonth = monthIndex !== undefined

  const occupanciesSum = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = currentRegionHasFlights && offer.offerFlightsEnabled && !offer.bundledWithFlightsOnly && occupanciesSum < 10 && !isFlightsDisabledForGeoState

  const capacities = useMemo(() => {
    return nonNullable(offer.packages.flatMap(pkg => pkg.roomRate?.capacities))
  }, [offer])

  const { view, hasRenderableRecommendations } = useCalendarRecommendationsView()
  const calendarRecommendationsEnabled = !!view && shouldEnableCalendarRecommendations(offer)

  const recommendationTitle = useMemo(() => {
    if (calendarRecommendationsEnabled && hasRenderableRecommendations) {
      return getRecommendationsTitle({
        checkIn: view?.checkIn,
        monthIndex: view?.monthIndex,
        duration: duration!,
        intl,
      }).title
    }
    return undefined
  }, [calendarRecommendationsEnabled, duration, hasRenderableRecommendations, intl, view?.checkIn, view?.monthIndex])

  const [dropdownTitle, dropdownSubtitle] = useMemo(() => {
    if (calendarRecommendationsEnabled) {
      return [
        recommendationTitle,
        undefined,
      ]
    }

    return [
      hasSelectedMonth
        ? intl.formatMessage({
            id: 'gptskx2IV80VHP3GarOU',
            defaultMessage: 'Select check-in date',
          })
        : intl.formatMessage({
            id: 'Cc2DFql8xX-Q75cyGZ2G',
            defaultMessage: 'Select check-in month',
          }),
      undefined,
    ] as const
  }, [calendarRecommendationsEnabled, hasSelectedMonth, recommendationTitle, intl])

  let headerExtensionSlot: ReactNode
  if (!calendarRecommendationsEnabled) {
    if (isCalendarBurnToggleVisible) {
      headerExtensionSlot = <OfferCalendarHeaderExtensionContainer>
        <OfferCalendarPointsToggle />
        <OfferCalendarDatePickerFlightDealBanner
          offer={offer}
          airport={airport}
          isFlightsActive={isAnyFlightsActive}
        />
      </OfferCalendarHeaderExtensionContainer>
    } else {
      headerExtensionSlot = <OfferCalendarDatePickerFlightDealBanner
        offer={offer}
        airport={airport}
        isFlightsActive={isAnyFlightsActive}
        wrapInHeaderExtensionContainer
      />
    }
  }

  return <DropdownSheetHeader
    size="M"
    alignment={calendarRecommendationsEnabled ? 'center' : 'start'}
    title={dropdownTitle}
    subtitle={dropdownSubtitle}
    data-testid="offer-calendar-date-picker"
    actionsSlot={!calendarRecommendationsEnabled && <Group direction="horizontal" gap={8} wrap="wrap">
      {subFilters.includes('capacity') && <OfferCalendarCapacitySelect capacities={capacities} disableMultiRoom={isVillaOffer(offer)} />}
      {!isMultipleRoomBookingActive && subFilters.includes('room') && <OfferCalendarRoomSelect />}
      {subFilters.includes('duration') && <OfferCalendarDurationSelect />}
      {flightsEnabled && subFilters.includes('airport') && <OfferCalendarFlightsToggle />}
      {isLuxPlusEnabled && offer.luxPlus.hasMemberPrices && <OfferCalendarLuxPlusToggle />}
    </Group>}
    extensionSlot={headerExtensionSlot}
    onDismissClick={onDismissClick}
    onBackClick={(hasSelectedMonth || calendarRecommendationsEnabled) ? onBackClick : undefined}
  />
}

export default withOfferPageAndReduxState((state, appState) => {
  return {
    offer: state.offer,
    duration: state.duration,
    rooms: state.rooms,
    isMultipleRoomBookingActive: isMultipleRoomBookingActive(state, appState),
    isLuxPlusEnabled: getIsLuxPlusEnabled(appState),
    airport: state.airport,
    isAnyFlightsActive: state.flights,
    open: !!state.activeFilter,
  }
}, OfferCalendarDatePickerDropdownHeader, preventRenderWithClosedFilter)
